import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, ChevronRight, ChevronLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface TourStep {
  title: string
  description: string
  emoji: string
}

const STUDENT_STEPS: TourStep[] = [
  {
    emoji: '👋',
    title: 'Bem-vindo ao MeuDriver!',
    description: 'Vamos te mostrar como aproveitar ao máximo a plataforma em menos de 1 minuto.',
  },
  {
    emoji: '🔍',
    title: 'Encontre seu instrutor',
    description: 'Use "Buscar instrutores" para filtrar por cidade, especialidade e preço. Você pode comparar até 3 instrutores lado a lado.',
  },
  {
    emoji: '📅',
    title: 'Agende sua aula',
    description: 'Escolha entre aula avulsa, experimental (30 min com 50% de desconto) ou pacote com até 20% de economia.',
  },
  {
    emoji: '💬',
    title: 'Chat com o instrutor',
    description: 'Cada aula tem um chat em tempo real para você combinar detalhes diretamente com seu instrutor.',
  },
  {
    emoji: '⭐',
    title: 'Avalie e acompanhe',
    description: 'Após cada aula, avalie o instrutor. Seu progresso rumo à CNH é acompanhado automaticamente no seu dashboard.',
  },
]

const INSTRUCTOR_STEPS: TourStep[] = [
  {
    emoji: '🎉',
    title: 'Bem-vindo ao MeuDriver!',
    description: 'Sua conta está aprovada. Veja como começar a receber alunos rapidamente.',
  },
  {
    emoji: '👤',
    title: 'Complete seu perfil',
    description: 'Um perfil completo com foto, bio detalhada e fotos do veículo recebe até 3x mais visualizações.',
  },
  {
    emoji: '🗓️',
    title: 'Configure sua agenda',
    description: 'Defina os dias e horários que você atende em "Agenda". Você pode bloquear datas específicas para folgas e feriados.',
  },
  {
    emoji: '📊',
    title: 'Acompanhe seu desempenho',
    description: 'No dashboard você tem KPIs em tempo real: faturamento, avaliação média, horários de pico e taxa de conclusão.',
  },
  {
    emoji: '💡',
    title: 'Dica: QR code do seu perfil',
    description: 'No dashboard há um QR code pronto para você imprimir e distribuir. Compartilhe em redes sociais para atrair mais alunos!',
  },
]

interface WelcomeTourProps {
  role: 'student' | 'instructor'
}

const STORAGE_KEY_PREFIX = 'md-tour-done-'

export function WelcomeTour({ role }: WelcomeTourProps) {
  const storageKey = STORAGE_KEY_PREFIX + role
  const [visible, setVisible] = useState(false)
  const [step, setStep] = useState(0)

  useEffect(() => {
    const done = localStorage.getItem(storageKey)
    if (!done) {
      const t = setTimeout(() => setVisible(true), 1200)
      return () => clearTimeout(t)
    }
  }, [storageKey])

  const steps = role === 'student' ? STUDENT_STEPS : INSTRUCTOR_STEPS

  function dismiss() {
    localStorage.setItem(storageKey, '1')
    setVisible(false)
  }

  function next() {
    if (step < steps.length - 1) setStep((s) => s + 1)
    else dismiss()
  }

  function prev() {
    if (step > 0) setStep((s) => s - 1)
  }

  return (
    <AnimatePresence>
      {visible && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
            onClick={dismiss}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 20 }}
            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
          >
            <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-7 pointer-events-auto relative">
              {/* Close */}
              <button
                onClick={dismiss}
                className="absolute top-4 right-4 h-7 w-7 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>

              {/* Step content */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 16 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -16 }}
                  transition={{ duration: 0.2 }}
                  className="text-center space-y-3"
                >
                  <div className="text-5xl">{steps[step].emoji}</div>
                  <h3 className="text-xl font-extrabold text-gray-900">{steps[step].title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed px-2">
                    {steps[step].description}
                  </p>
                </motion.div>
              </AnimatePresence>

              {/* Progress dots */}
              <div className="flex justify-center gap-1.5 mt-6 mb-5">
                {steps.map((_, i) => (
                  <span
                    key={i}
                    className={`h-1.5 rounded-full transition-all ${
                      i === step ? 'w-5 bg-brand-600' : 'w-1.5 bg-gray-200'
                    }`}
                  />
                ))}
              </div>

              {/* Navigation */}
              <div className="flex items-center gap-3">
                {step > 0 ? (
                  <Button variant="outline" size="sm" onClick={prev} className="gap-1">
                    <ChevronLeft className="h-3.5 w-3.5" />
                    Anterior
                  </Button>
                ) : (
                  <Button variant="ghost" size="sm" onClick={dismiss} className="text-muted-foreground">
                    Pular tour
                  </Button>
                )}
                <Button onClick={next} className="flex-1 gap-1">
                  {step < steps.length - 1 ? (
                    <>Próximo <ChevronRight className="h-3.5 w-3.5" /></>
                  ) : (
                    'Começar 🚀'
                  )}
                </Button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
